import { Box, ButtonBase, Typography, TextField, Button, IconButton, Dialog, DialogContent, DialogTitle } from "@mui/material";
import { useState, useEffect } from "react";
import { useAuth } from "../../auth/AuthContext";
import CloseIcon from '@mui/icons-material/Close';
import ActionButton from '../../ActionButton';

const OurTestimonials = ({ testimon = [], companyLogo = "https://via.placeholder.com/100" }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const activeTestimonials = testimon.filter(t => t.status === 1);
  const groupSize = 2;
  const { addData } = useAuth();

  // Calculate the groups of testimonials
  const testimonialGroups = [];
  for (let i = 0; i < activeTestimonials.length; i += groupSize) {
    // Get the current group
    let currentGroup = activeTestimonials.slice(i, i + groupSize);
    
    // If this is the last group and it has only one testimonial, duplicate one from the previous group
    if (i + groupSize > activeTestimonials.length && currentGroup.length === 1) {
      // Check if there's a previous group to duplicate from
      if (testimonialGroups.length > 0) {
        // Duplicate the first testimonial from the previous group
        const previousGroup = testimonialGroups[testimonialGroups.length - 1];
        currentGroup.push({ ...previousGroup[0], id: `duplicate-${previousGroup[0].id}` });
      }
    }
    
    testimonialGroups.push(currentGroup);
  }

  // Form state to match the testimonials_V2 table schema
  const [formData, setFormData] = useState({
    client_name: '',
    description: '',
    image: null,
  });

  const handleAddClick = () => {
    setFormData({
      client_name: '',
      description: '',
      image: null,
    });
    setOpenAddDialog(true);
  };

  const handleClose = () => {
    setOpenAddDialog(false);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({
      ...prev,
      image: e.target.files[0]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await addData('proccess.php', 'testmonials_V2', formData);
      if (response.success) {
        setOpenAddDialog(false);
      } else {
        console.error(response.message || "Failed to add testimonial");
      }
    } catch (err) {
      console.error("Operation error:", err);
    }
  };

  // Auto-rotate every 5 seconds
  useEffect(() => {
    if (testimonialGroups.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        (prevIndex + 1) % testimonialGroups.length
      );
    }, 20000);

    return () => clearInterval(interval);
  }, [testimonialGroups.length]);

  return (
    <Box
      sx={{
        width: "100%",
        height: "auto",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: { xs: "20px", sm: "40px" },
        backgroundColor: '#fff'
      }}
    >
      <h1 
          className='primary-header'
          style={{color: '#3b76b2', textAlign: 'center'}}
          data-aos="fade-down"
          data-aos-delay="200"
        >
          What Our Clients Say
        </h1>
      <Box 
        sx={{ 
          width: "100%", 
          maxWidth: "1200px", 
          position: 'relative',
          overflow: 'hidden',
          minHeight: '200px',
          paddingTop: '2rem'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1rem',
          }}
        >
          {testimonialGroups.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                gap: '1rem',
                justifyContent: 'center',
                alignItems: 'center',
              }}
              flexDirection={{xs: 'column', sm: 'column', md: 'row'}}
            >
              {testimonialGroups[currentIndex].map((testimonial, index) => (
                <Box display={'flex'} alignItems={'center'} flexDirection={{xs: 'column', sm: 'column', md: 'row'}} key={testimonial.id}>
                    <Box
                        boxShadow={2}
                        sx={{
                        width: '100%',
                        maxWidth: {xs: '300px', sm: '400px', md: '350px'},
                        minWidth: {xs: '300px', sm: '400px', md: '350px'},
                        height: {xs: '300px', sm: '200px', md: '230px'},
                        background: '#fff',
                        padding: '20px',
                        margin: '1rem',
                        borderRadius: '8px',
                        textAlign: 'center',
                        }}
                    >
                        <Box sx={{ mb: 2 }} display={'flex'} position={'relative'}> 
                        <Box boxShadow={2} sx={{ width: '60px', height: '60px', margin: '0', position: 'absolute', top: '-30px', left: '-30px', borderRadius: '50%', border: '.3rem solid #ffffffff', backgroundColor: '#fff' }}>
                            <img 
                            src={`https://mindsinaction.com.na/assets/media/pictures/${testimonial.image}`} 
                            alt={testimonial.client_name}
                            style={{ 
                                width: '100%', 
                                height: '100%', 
                                borderRadius: '50%', 
                                objectFit: 'cover' 
                            }}
                            />
                        </Box>
                        <Typography 
                            variant="h6" 
                            sx={{ ml: '3rem', fontWeight: 600 }}
                        >
                            {testimonial.client_name}
                        </Typography>
                        </Box>
                        <Box width={"100%"} height={"100%"} overflow={"auto"}>
                          <Typography 
                              variant="body1"
                              sx={{ fontStyle: 'italic', color: '#555' }}
                          >
                              "{testimonial.description}"
                          </Typography>
                        </Box>
                    </Box>
                    {index === 0 && testimonialGroups[currentIndex].length > 1 && (
                        <Box width={'150px'} height={'150px'} position={'relative'} display={'flex'} alignItems={'center'} justifyContent={'center'}>
                            <Box
                                position={'absolute'}
                                top={0}
                                left={0}
                                borderTop={{xs: 'none', sm: 'none', md: '.2rem solid #1ddd2dff'}}
                                borderRight={{xs: '.2rem solid #1ddd2dff', sm: '.2rem solid #1ddd2dff'}}
                                width={{xs: '50%', sm: '50%', md: '50%'}}
                                height={{xs: '3rem', sm: '50px', md: '50px'}}
                            ></Box>
                            <Box
                                position={'absolute'}
                                top={'-3px'}
                                left={{xs: '46.8%', sm: '46.8%', md: '0'}}
                                sx={{backgroundColor: '#1ddd2dff'}}
                                width={'8px'}
                                height={'8px'}
                            ></Box>
                            <Box
                                sx={{
                                    flex: { xs: '1 0 100%', sm: '0 0 auto' },
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    maxWidth: '100px',
                                    margin: '1rem'
                                }}
                            >
                                <img
                                    src={companyLogo}
                                    alt="Company Logo"
                                    style={{
                                    maxWidth: '100%',
                                    height: 'auto',
                                    objectFit: 'contain'
                                    }}
                                />
                            </Box>
                            <Box
                                position={'absolute'}
                                right={0}
                                bottom={0}
                                borderBottom={{xs: 'none', sm: 'none', md: '.2rem solid #1ddd2dff'}}
                                borderLeft={{xs: '.2rem solid #1ddd2dff', sm: '.2rem solid #1ddd2dff'}}
                                width={{xs: '50%', sm: '50%', md: '50%'}}
                                height={{xs: '3rem', sm: '50px', md: '50px'}}
                            ></Box>
                            <Box
                                position={'absolute'}
                                bottom={'-3px'}
                                right={{xs: '47%', sm: '47%', md: '0'}}
                                sx={{backgroundColor: '#1ddd2dff'}}
                                width={'8px'}
                                height={'8px'}
                            ></Box>
                        </Box>
                    )}
                </Box>
              ))}
            </Box>
          )}
          
          <ActionButton tittle="Give Testmonial" btnwidth="170px" bcolor="#3b76b2" onClick={handleAddClick} tcolor="#fff" />
          
        </Box>

        {/* Add testimonials dialog */}
        <Dialog
          open={openAddDialog}
          onClose={handleClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Add New Testimonial
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 2 }}
            >
              <TextField
                label="Client Name"
                name="client_name"
                value={formData.client_name}
                onChange={handleFormChange}
                required
                fullWidth
              />
              <TextField
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleFormChange}
                multiline
                rows={4}
                required
                fullWidth
              />
              <Button
                component="label"
                variant="outlined"
                sx={{ textTransform: 'none' }}
              >
                Upload Image
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleFileChange}
                />
              </Button>
              {formData.image && (
                <Typography variant="caption">
                  Selected file: {formData.image.name}
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  onClick={handleClose}
                  variant="outlined"
                  sx={{ textTransform: 'none' }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{ 
                    textTransform: 'none',
                    backgroundColor: '#134e9c',
                    '&:hover': { backgroundColor: '#1b09bd' }
                  }}
                >
                  Submit
                </Button>
              </Box>
            </Box>
          </DialogContent>
        </Dialog>
      </Box>
    </Box>
  );
};

export default OurTestimonials;